import { useCallback } from 'react';

import { chunk } from 'lodash-es';

import type { EndpointResponse } from '@iconik-io/iconik-api/src/client/response';
import { httpPostSearch } from '@iconik-io/iconik-api/src/services/search';
import {
  httpGetGroupsBasic,
  httpGetRoleGroupsBasic,
  httpGetTeamsBasic,
  httpGetUsersBasic,
} from '@iconik-io/iconik-api/src/services/users';

import type { EndpointResponseNormalized } from '../../../redux/middlewares/http_normalizer/types';
import { useAppDispatch } from '../../../redux/redux_hooks';
import {
  type UseEntityOptions,
  type UseEntityResult,
  useEntity,
} from './use_entity';

const entityRequestsMap = {
  users_basic: (ids: string[]) =>
    httpGetUsersBasic({ ids: ids.join(','), per_page: 50 }),
  groups_basic: (ids: string[]) =>
    httpGetGroupsBasic({ ids: ids.join(','), per_page: 50 }),
  teams_basic: (ids: string[]) =>
    httpGetTeamsBasic({ ids: ids.join(','), per_page: 50 }),
  role_groups_basic: (ids: string[]) =>
    httpGetRoleGroupsBasic({ ids: ids.join(','), per_page: 50 }),
  // Assets and collections are fetched by id through search.
  search_items: (ids: string[]) =>
    httpPostSearch(
      {
        doc_types: ['assets', 'collections'],
        filter: { operator: 'OR', terms: [{ name: 'id', value_in: ids }] },
      },
      { per_page: 50, save_search_history: false },
    ),
};

type BatchEntityType = keyof typeof entityRequestsMap;

type UseEntityBatchOptions<
  EntityType extends BatchEntityType,
  Result extends EndpointResponseNormalized,
> = Omit<UseEntityOptions<EntityType, Result>, 'getItem'>;

const entityIdsMap: Record<BatchEntityType, Set<string>> = {
  users_basic: new Set(),
  groups_basic: new Set(),
  teams_basic: new Set(),
  role_groups_basic: new Set(),
  search_items: new Set(),
};

const entityQueuedIdsMap: Record<BatchEntityType, Set<string>> = {
  users_basic: new Set(),
  groups_basic: new Set(),
  teams_basic: new Set(),
  role_groups_basic: new Set(),
  search_items: new Set(),
};

const entityInFlightMap: Record<BatchEntityType, boolean> = {
  users_basic: false,
  groups_basic: false,
  teams_basic: false,
  role_groups_basic: false,
  search_items: false,
};

const entityTimeoutMap = new Map<BatchEntityType, number>();

export function useEntityBatch<
  EntityType extends BatchEntityType,
  Result extends EndpointResponseNormalized,
>(
  options: UseEntityBatchOptions<EntityType, Result>,
): UseEntityResult<EntityType> {
  const dispatch = useAppDispatch();

  const getItem = useCallback(() => {
    const primaryId = options.primaryId;
    if (!primaryId) {
      return Promise.reject(new Error('Primary ID is not specified'));
    }
    clearTimeout(entityTimeoutMap.get(options.entityType));

    if (entityInFlightMap[options.entityType]) {
      entityQueuedIdsMap[options.entityType].add(primaryId);
    } else {
      entityIdsMap[options.entityType].add(primaryId);
    }

    const request = entityRequestsMap[options.entityType];

    return new Promise<EndpointResponse<ReturnType<typeof request>>>(
      (resolve, reject) => {
        entityTimeoutMap.set(
          options.entityType,
          window.setTimeout(() => {
            const idChunkIndex: Record<string, number> = {};
            entityTimeoutMap.delete(options.entityType);

            const idChunks = chunk([...entityIdsMap[options.entityType]], 50);
            const promises = [];
            for (let i = 0; i < idChunks.length; i++) {
              const idChunk = idChunks[i];
              idChunk.forEach((id) => {
                idChunkIndex[id] = i;
              });
              promises.push(dispatch(request(idChunk)));
            }

            entityInFlightMap[options.entityType] = true;

            Promise.all(promises)
              .then((result) => {
                const resultElement = result[idChunkIndex[primaryId]];
                resolve(
                  // biome-ignore lint/plugin: the batched promises share one endpoint, but Promise.all widens the results to their union.
                  resultElement as EndpointResponse<ReturnType<typeof request>>,
                );

                entityIdsMap[options.entityType] =
                  entityQueuedIdsMap[options.entityType];

                entityQueuedIdsMap[options.entityType] = new Set();
              })
              .catch(reject)
              .finally(() => {
                entityInFlightMap[options.entityType] = false;
              });
          }, 50),
        );
      },
    );
  }, [options.primaryId, options.entityType]);

  return useEntity({
    entityType: options.entityType,
    primaryId: options.primaryId,
    getItem,
  });
}
